/**
 * Revenue Admin — dashboard de ingresos consolidado.
 *
 * Regla de display:
 *   - Stripe SIEMPRE en USD (EUR convertido a USD vía 1/0.85 ≈ 1.176).
 *   - MercadoPago SIEMPRE en ARS (los pagos ya están en ARS nativo).
 *   - El total combinado se muestra en ARS equivalente (Stripe USD × tasa).
 *
 * Esto se logra con dos llamadas paralelas al endpoint:
 *   - "usd" call → tasas usd_to_ars=1, eur_to_ars=1/0.85 → totales/timeseries
 *     con `revenue_cents` ya en USD-cents. Tomamos solo .stripe.
 *   - "ars" call → tasas usd_to_ars=1.420, eur_to_ars=1.420/0.85. Tomamos solo
 *     .mercadopago (que está en ARS nativo, no se altera).
 *
 * Acceso: super_admin (ver ProtectedAdminRoute en App.tsx).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  CalendarRange,
  CreditCard,
  DollarSign,
  Loader2,
  RefreshCw,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import {
  AdminEmptyState,
  AdminGlassCard,
  AdminKpiCard,
} from "@/components/admin/AdminGlass";
import {
  formatArsCents,
  formatArsMajorCompact,
  formatCount,
  formatCurrencyCents,
  formatUsdCents,
  formatUsdMajorCompact,
  gatewayMoneyLabel,
} from "@/lib/adminMoneyFormat";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import {
  CONTRACT_PERIODS,
  CONTRACT_START,
  CONTRACT_END,
  DEFAULT_EUR_TO_ARS,
  DEFAULT_EUR_TO_USD,
  DEFAULT_USD_TO_ARS,
  findCurrentPeriod,
  type ContractPeriod,
} from "@/config/revenueContract";

interface RevenueGateway {
  tx_count: number;
  revenue_cents: number;
  unique_customers: number;
  tx_in_window: number;
  revenue_cents_window: number;
  by_currency: Array<{ currency: string; tx_count: number; revenue_cents: number }>;
}

interface RevenueResponse {
  success: boolean;
  source: string | null;
  granularity: "day" | "week";
  periods: number;
  window_days: number;
  window_start: string | null;
  window_end: string | null;
  target_currency: "ars" | "usd";
  rates: { usd_to_ars: number; eur_to_ars: number };
  stripe: RevenueGateway;
  /** Live Checkout Sessions (metadata.source=brand) when stripe_transactions lags. */
  stripe_live?: {
    ok: boolean;
    error: string | null;
    days: number;
    buyers: number;
    revenue_cents: number;
    note?: string;
  };
  mercadopago: RevenueGateway;
  timeseries: Array<{
    bucket: string;
    stripe_revenue_cents: number;
    mp_revenue_cents: number;
    stripe_tx: number;
    mp_tx: number;
  }>;
}

const WEEK_OPTIONS: Array<{ label: string; value: number }> = [
  { label: "Últimas 4 semanas", value: 4 },
  { label: "Últimas 12 semanas", value: 12 },
  { label: "Últimas 26 semanas", value: 26 },
  { label: "Últimas 52 semanas", value: 52 },
];

const RECENT_DAY_OPTIONS: Array<{ label: string; days: number }> = [
  { label: "Últimos 7 días", days: 7 },
  { label: "Últimos 30 días", days: 30 },
  { label: "Últimos 90 días", days: 90 },
];

// Tasas para "convert to USD" — EUR a USD usando el rate del checkout (0.85).
const EUR_TO_USD = 1 / DEFAULT_EUR_TO_USD; // ≈ 1.176

const chartConfig = {
  stripe: {
    label: "Stripe (USD)",
    color: "hsl(220 90% 60%)",
  },
  mercadopago: {
    label: "MercadoPago (ARS)",
    color: "hsl(187 85% 53%)",
  },
} satisfies ChartConfig;

const PIE_COLORS = [chartConfig.stripe.color, chartConfig.mercadopago.color];

const fmtUsd = formatUsdCents;
const fmtUsdCompact = formatUsdMajorCompact;
const fmtArs = formatArsCents;
const fmtArsCompact = formatArsMajorCompact;
const fmtNumber = formatCount;
const fmtCurrency = formatCurrencyCents;

function fmtDayShort(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "short", timeZone: "UTC" });
}

function fmtDayLong(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function fmtDayMedium(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "long", timeZone: "UTC" });
}

function fmtWeekRange(iso: string): string {
  const start = new Date(`${iso}T00:00:00Z`);
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const startStr = start.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
  const endStr = end.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
  return `${startStr}–${endStr}`;
}

function daysBetweenInclusive(startIso: string, endIso: string): number {
  const a = new Date(`${startIso}T00:00:00Z`).getTime();
  const b = new Date(`${endIso}T00:00:00Z`).getTime();
  return Math.round((b - a) / (24 * 60 * 60 * 1000)) + 1;
}

type MainSelection =
  | { kind: "contract"; period: ContractPeriod }
  | { kind: "all-contract" }
  | { kind: "recent"; days: number };

const todayArgIso = (): string => {
  const now = new Date();
  const arg = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  return arg.toISOString().slice(0, 10);
};

export default function RevenueAdmin() {
  const { toast } = useToast();
  const { getAuthHeaders, isLoaded, isSignedIn } = useAdminAuth();

  const initialSelection: MainSelection = useMemo(() => {
    const today = todayArgIso();
    const current = findCurrentPeriod(today);
    if (current) return { kind: "contract", period: current };
    if (today < CONTRACT_START) return { kind: "contract", period: CONTRACT_PERIODS[0] };
    return { kind: "contract", period: CONTRACT_PERIODS[CONTRACT_PERIODS.length - 1] };
  }, []);

  const [mainSelection, setMainSelection] = useState<MainSelection>(initialSelection);
  const [weeklyWeeks, setWeeklyWeeks] = useState<number>(12);
  const [mainView, setMainView] = useState<"revenue" | "tx">("revenue");
  const [weeklyView, setWeeklyView] = useState<"revenue" | "tx">("revenue");

  const [mainUsd, setMainUsd] = useState<RevenueResponse | null>(null);
  const [mainArs, setMainArs] = useState<RevenueResponse | null>(null);
  const [weeklyUsd, setWeeklyUsd] = useState<RevenueResponse | null>(null);
  const [weeklyArs, setWeeklyArs] = useState<RevenueResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Tasas para que el endpoint devuelva totales en USD vs ARS.
  // USD-call: usd=1, eur=1/0.85 → revenue_cents queda en USD-cents nativo.
  const usdRatesQs = `usd_to_ars=1&eur_to_ars=${EUR_TO_USD}`;
  // ARS-call: usd=1420, eur=1420/0.85 → revenue_cents queda en ARS-cents.
  const arsRatesQs = `usd_to_ars=${DEFAULT_USD_TO_ARS}&eur_to_ars=${DEFAULT_EUR_TO_ARS}`;

  const mainBaseQs = useMemo(() => {
    if (mainSelection.kind === "contract") {
      return `granularity=day&start_date=${mainSelection.period.start}&end_date=${mainSelection.period.end}`;
    }
    if (mainSelection.kind === "all-contract") {
      return `granularity=day&start_date=${CONTRACT_START}&end_date=${CONTRACT_END}`;
    }
    return `granularity=day&periods=${mainSelection.days}`;
  }, [mainSelection]);

  const weeklyBaseQs = useMemo(() => `granularity=week&periods=${weeklyWeeks}`, [weeklyWeeks]);

  const loadRevenue = useCallback(async () => {
    if (!isLoaded || !isSignedIn) return;
    setIsLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const [mUsdRes, mArsRes, wUsdRes, wArsRes] = await Promise.all([
        fetch(`/api/admin/revenue?${mainBaseQs}&${usdRatesQs}`, { headers }),
        fetch(`/api/admin/revenue?${mainBaseQs}&${arsRatesQs}`, { headers }),
        fetch(`/api/admin/revenue?${weeklyBaseQs}&${usdRatesQs}`, { headers }),
        fetch(`/api/admin/revenue?${weeklyBaseQs}&${arsRatesQs}`, { headers }),
      ]);

      const handle = async (r: Response) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          const msg = (body as { message?: string; error?: string }).message
            || (body as { error?: string }).error
            || `HTTP ${r.status}`;
          throw new Error(msg);
        }
        return (await r.json()) as RevenueResponse;
      };

      const [mUsd, mArs, wUsd, wArs] = await Promise.all([
        handle(mUsdRes),
        handle(mArsRes),
        handle(wUsdRes),
        handle(wArsRes),
      ]);
      setMainUsd(mUsd);
      setMainArs(mArs);
      setWeeklyUsd(wUsd);
      setWeeklyArs(wArs);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error desconocido";
      setError(msg);
      toast({
        title: "No se pudo cargar el revenue",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [mainBaseQs, weeklyBaseQs, usdRatesQs, arsRatesQs, getAuthHeaders, isLoaded, isSignedIn, toast]);

  useEffect(() => {
    void loadRevenue();
  }, [loadRevenue]);

  // Stripe siempre del payload USD; MP siempre del payload ARS.
  const stripeAllUsdCents = mainUsd?.stripe.revenue_cents ?? 0;
  const stripeWindowUsdCents = mainUsd?.stripe.revenue_cents_window ?? 0;
  const mpAllArsCents = mainArs?.mercadopago.revenue_cents ?? 0;
  const mpWindowArsCents = mainArs?.mercadopago.revenue_cents_window ?? 0;

  // Para mix/comparación: convertir Stripe USD a ARS equivalente.
  const stripeAllArsCents = Math.round(stripeAllUsdCents * DEFAULT_USD_TO_ARS);
  const stripeWindowArsCents = Math.round(stripeWindowUsdCents * DEFAULT_USD_TO_ARS);
  const totalCombinedArsCents = stripeAllArsCents + mpAllArsCents;
  const totalWindowCombinedArsCents = stripeWindowArsCents + mpWindowArsCents;
  const stripeShareArs = totalCombinedArsCents > 0
    ? (stripeAllArsCents / totalCombinedArsCents) * 100
    : 0;
  const mpShareArs = totalCombinedArsCents > 0
    ? (mpAllArsCents / totalCombinedArsCents) * 100
    : 0;

  const stripeMainSeries = useMemo(() => {
    if (!mainUsd) return [];
    return mainUsd.timeseries.map((d) => ({
      bucket: d.bucket,
      label: fmtDayShort(d.bucket),
      stripe: d.stripe_revenue_cents / 100, // USD
      stripe_tx: d.stripe_tx,
    }));
  }, [mainUsd]);

  const mpMainSeries = useMemo(() => {
    if (!mainArs) return [];
    return mainArs.timeseries.map((d) => ({
      bucket: d.bucket,
      label: fmtDayShort(d.bucket),
      mercadopago: d.mp_revenue_cents / 100, // ARS
      mercadopago_tx: d.mp_tx,
    }));
  }, [mainArs]);

  const stripeWeeklySeries = useMemo(() => {
    if (!weeklyUsd) return [];
    return weeklyUsd.timeseries.map((d) => ({
      bucket: d.bucket,
      label: fmtWeekRange(d.bucket),
      stripe: d.stripe_revenue_cents / 100,
      stripe_tx: d.stripe_tx,
    }));
  }, [weeklyUsd]);

  const mpWeeklySeries = useMemo(() => {
    if (!weeklyArs) return [];
    return weeklyArs.timeseries.map((d) => ({
      bucket: d.bucket,
      label: fmtWeekRange(d.bucket),
      mercadopago: d.mp_revenue_cents / 100,
      mercadopago_tx: d.mp_tx,
    }));
  }, [weeklyArs]);

  const mainTitleSuffix = useMemo(() => {
    if (mainSelection.kind === "contract") {
      const p = mainSelection.period;
      const days = daysBetweenInclusive(p.start, p.end);
      return `${p.label}: ${fmtDayMedium(p.start)} → ${fmtDayMedium(p.end)} (${days} días)`;
    }
    if (mainSelection.kind === "all-contract") {
      const days = daysBetweenInclusive(CONTRACT_START, CONTRACT_END);
      return `Vigencia: ${fmtDayMedium(CONTRACT_START)} → ${fmtDayMedium(CONTRACT_END)} (${days} días)`;
    }
    return `Últimos ${mainSelection.days} días`;
  }, [mainSelection]);

  const handleSelectChange = (value: string) => {
    if (value === "all-contract") {
      setMainSelection({ kind: "all-contract" });
      return;
    }
    if (value.startsWith("recent-")) {
      const days = Number(value.slice("recent-".length));
      if (Number.isFinite(days)) setMainSelection({ kind: "recent", days });
      return;
    }
    if (value.startsWith("period-")) {
      const idx = Number(value.slice("period-".length));
      const period = CONTRACT_PERIODS.find((p) => p.index === idx);
      if (period) setMainSelection({ kind: "contract", period });
    }
  };

  const selectedValue =
    mainSelection.kind === "contract"
      ? `period-${mainSelection.period.index}`
      : mainSelection.kind === "all-contract"
        ? "all-contract"
        : `recent-${mainSelection.days}`;

  const pieData = useMemo(() => {
    return [
      { name: "Stripe", value: stripeAllArsCents, fill: chartConfig.stripe.color },
      { name: "MercadoPago", value: mpAllArsCents, fill: chartConfig.mercadopago.color },
    ].filter((d) => d.value > 0);
  }, [stripeAllArsCents, mpAllArsCents]);

  const stripeTotalsTx = mainUsd?.stripe.tx_count ?? 0;
  const stripeTotalsCustomers = mainUsd?.stripe.unique_customers ?? 0;
  const mpTotalsTx = mainArs?.mercadopago.tx_count ?? 0;
  const mpTotalsCustomers = mainArs?.mercadopago.unique_customers ?? 0;
  const stripeWindowTx = mainUsd?.stripe.tx_in_window ?? 0;
  const mpWindowTx = mainArs?.mercadopago.tx_in_window ?? 0;
  const stripeLive = mainUsd?.stripe_live;
  const stripeLiveHint =
    stripeLive?.ok && (stripeLive.buyers ?? 0) > 0
      ? ` · Checkout live ${stripeLive.buyers} pagos · ${fmtUsd(stripeLive.revenue_cents)} (${stripeLive.days}d)`
      : "";

  return (
    <AdminPageShell width="xl">
      <AdminPageHeader
        description={
          <span>
            <strong>Revenue de producto</strong> del sitio (no wallet personal).{" "}
            <strong>{gatewayMoneyLabel("stripe")}</strong>
            {" · "}
            <strong>{gatewayMoneyLabel("mercadopago")}</strong> solo cobros brand{" "}
            <code>source = {mainUsd?.source ?? "anaafernandez"}</code>
            {stripeLiveHint}. Contrato:{" "}
            <strong>
              {fmtDayMedium(CONTRACT_START)} → {fmtDayMedium(CONTRACT_END)}
            </strong>
            . Chargebacks → Disputas · Compradores → /admin/buyers. Finanzas personales
            (Uber, QR, transferencias) → DB Railway <code>personal-finance</code>.
          </span>
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={loadRevenue}
            disabled={isLoading}
            className="border-white/10 bg-white/[0.04] text-white/75 hover:bg-white/[0.08] hover:text-white"
          >
            {isLoading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Actualizar
          </Button>
        }
      />

      {error && !isLoading ? (
        <Alert className="border-red-400/30 bg-red-500/10 text-white">
          <AlertTriangle className="h-4 w-4 text-red-300" />
          <AlertTitle className="text-white/90">No se pudo cargar el revenue</AlertTitle>
          <AlertDescription className="text-white/55">{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <AdminKpiCard
          label={gatewayMoneyLabel("stripe")}
          icon={CreditCard}
          iconColor="text-[hsl(220_90%_70%)]"
          glowColor="bg-[hsl(220_90%_60%)]/20"
          accent="stripe"
          value={isLoading ? null : fmtUsd(stripeAllUsdCents)}
          subValue={isLoading ? null : `≈ ${fmtArs(stripeAllArsCents)} ARS eq.`}
          hint={
            isLoading
              ? null
              : `${fmtNumber(stripeTotalsTx)} pagos · ${fmtNumber(stripeTotalsCustomers)} clientes`
          }
          loading={isLoading && !mainUsd}
        />
        <AdminKpiCard
          label={gatewayMoneyLabel("mercadopago")}
          icon={Wallet}
          iconColor="text-[hsl(187_85%_60%)]"
          glowColor="bg-[hsl(187_85%_53%)]/20"
          accent="mp"
          value={isLoading ? null : fmtArs(mpAllArsCents)}
          hint={
            isLoading
              ? null
              : `${fmtNumber(mpTotalsTx)} pagos · ${fmtNumber(mpTotalsCustomers)} clientes · nativo ARS`
          }
          loading={isLoading && !mainArs}
        />
        <AdminKpiCard
          label="Total combinado · ARS eq."
          icon={DollarSign}
          iconColor="text-white/70"
          glowColor="bg-white/10"
          accent="neutral"
          value={isLoading ? null : fmtArs(totalCombinedArsCents)}
          hint={
            isLoading
              ? null
              : `Ventana: ${fmtArs(totalWindowCombinedArsCents)} · Stripe convertido a ARS + MP nativo`
          }
          loading={isLoading && !(mainUsd && mainArs)}
        />
        <AdminKpiCard
          label="Clientes únicos"
          icon={Users}
          iconColor="text-emerald-300"
          glowColor="bg-emerald-500/15"
          value={isLoading ? null : fmtNumber(stripeTotalsCustomers + mpTotalsCustomers)}
          hint={
            isLoading
              ? null
              : `${fmtNumber(stripeTotalsTx + mpTotalsTx)} transacciones · suma de gateways`
          }
          loading={isLoading && !(mainUsd && mainArs)}
        />
      </div>

      {/* Main chart — selector único, dos charts lado a lado por moneda nativa */}
      <Card className="border-white/[0.08] bg-white/[0.03] text-white shadow-none backdrop-blur-sm">
        <CardHeader className="space-y-3 border-b border-white/[0.06] pb-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 space-y-1.5">
              <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xl font-semibold tracking-tight text-white">
                <span className="inline-flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-white/70" />
                  Evolución de ingresos
                </span>
                <span className="text-sm font-medium text-white/45">
                  — {mainTitleSuffix}
                </span>
              </CardTitle>
              <CardDescription className="text-xs leading-relaxed text-white/45">
                {gatewayMoneyLabel("stripe")} nativo (EUR→USD a 0,85) · {gatewayMoneyLabel("mercadopago")} nativo — sin mezclar monedas en el mismo eje
              </CardDescription>
              {!isLoading ? (
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 pt-1 text-sm">
                  <span style={{ color: chartConfig.stripe.color }}>
                    Stripe período: <span className="tabular-nums font-semibold text-white/90">{fmtUsd(stripeWindowUsdCents)}</span>{" "}
                    <span className="text-xs text-white/40">
                      ({fmtNumber(stripeWindowTx)} pagos)
                    </span>
                  </span>
                  <span style={{ color: chartConfig.mercadopago.color }}>
                    MP período: <span className="tabular-nums font-semibold text-white/90">{fmtArs(mpWindowArsCents)}</span>{" "}
                    <span className="text-xs text-white/40">
                      ({fmtNumber(mpWindowTx)} pagos)
                    </span>
                  </span>
                </div>
              ) : (
                <Skeleton className="mt-1 h-4 w-72 bg-white/[0.07]" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={selectedValue} onValueChange={handleSelectChange}>
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Seleccionar período" />
                </SelectTrigger>
                <SelectContent className="max-h-[420px]">
                  <SelectGroup>
                    <SelectLabel>Contrato — cortes de 30 días</SelectLabel>
                    {CONTRACT_PERIODS.map((p) => {
                      const days = daysBetweenInclusive(p.start, p.end);
                      return (
                        <SelectItem key={p.index} value={`period-${p.index}`}>
                          {p.label}: {fmtDayMedium(p.start)} → {fmtDayMedium(p.end)} · {days}d
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>Vigencia completa</SelectLabel>
                    <SelectItem value="all-contract">
                      Todo el contrato — 360 días
                    </SelectItem>
                  </SelectGroup>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel>Recientes</SelectLabel>
                    {RECENT_DAY_OPTIONS.map((o) => (
                      <SelectItem key={o.days} value={`recent-${o.days}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <Tabs value={mainView} onValueChange={(v) => setMainView(v as "revenue" | "tx")}>
                <TabsList>
                  <TabsTrigger value="revenue">Ingresos</TabsTrigger>
                  <TabsTrigger value="tx">Pagos</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 pt-6 lg:grid-cols-2">
          <SingleGatewayChart
            gateway="stripe"
            currencyLabel="USD"
            view={mainView}
            isLoading={isLoading}
            series={stripeMainSeries}
            color={chartConfig.stripe.color}
            fmtCompact={fmtUsdCompact}
            fmtFull={fmtUsd}
          />
          <SingleGatewayChart
            gateway="mercadopago"
            currencyLabel="ARS"
            view={mainView}
            isLoading={isLoading}
            series={mpMainSeries}
            color={chartConfig.mercadopago.color}
            fmtCompact={fmtArsCompact}
            fmtFull={fmtArs}
          />
        </CardContent>
      </Card>

      {/* Weekly charts */}
      <Card className="border-white/[0.08] bg-white/[0.03] text-white shadow-none backdrop-blur-sm">
        <CardHeader className="flex flex-col gap-3 space-y-0 border-b border-white/[0.06] pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-base text-white">
              <CalendarRange className="h-4 w-4 text-white/50" />
              Vista semanal
            </CardTitle>
            <CardDescription className="text-white/45">
              {gatewayMoneyLabel("stripe")} · {gatewayMoneyLabel("mercadopago")} — semana ISO ·{" "}
              {WEEK_OPTIONS.find((o) => o.value === weeklyWeeks)?.label ?? `${weeklyWeeks} semanas`}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(weeklyWeeks)}
              onValueChange={(v) => setWeeklyWeeks(Number(v))}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEK_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={String(o.value)}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Tabs value={weeklyView} onValueChange={(v) => setWeeklyView(v as "revenue" | "tx")}>
              <TabsList>
                <TabsTrigger value="revenue">Ingresos</TabsTrigger>
                <TabsTrigger value="tx">Pagos</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SingleGatewayChart
            gateway="stripe"
            currencyLabel="USD"
            view={weeklyView}
            isLoading={isLoading}
            series={stripeWeeklySeries}
            color={chartConfig.stripe.color}
            fmtCompact={fmtUsdCompact}
            fmtFull={fmtUsd}
            weekly
          />
          <SingleGatewayChart
            gateway="mercadopago"
            currencyLabel="ARS"
            view={weeklyView}
            isLoading={isLoading}
            series={mpWeeklySeries}
            color={chartConfig.mercadopago.color}
            fmtCompact={fmtArsCompact}
            fmtFull={fmtArs}
            weekly
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="border-white/[0.08] bg-white/[0.03] text-white shadow-none backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white">Mix por gateway</CardTitle>
            <CardDescription className="text-white/45">
              Stripe USD → ARS eq. para comparar (tasa {DEFAULT_USD_TO_ARS.toLocaleString("es-AR")} ARS/USD). Barras nativas siguen en moneda propia.
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-4">
            {isLoading ? (
              <Skeleton className="h-[260px] w-full bg-white/[0.07]" />
            ) : pieData.length === 0 ? (
              <EmptyChart label="Sin pagos registrados" height={260} />
            ) : (
              <ChartContainer config={chartConfig} className="mx-auto h-[260px] w-full">
                <PieChart>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        formatter={(value, name) => [` ${fmtArs(Number(value))}`, String(name)]}
                        nameKey="name"
                      />
                    }
                  />
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={56}
                    outerRadius={92}
                    strokeWidth={2}
                  >
                    {pieData.map((entry, idx) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={PIE_COLORS[idx % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ChartContainer>
            )}
            {!isLoading ? (
              <div className="mt-4 space-y-2">
                <ShareRow
                  label={gatewayMoneyLabel("stripe")}
                  color={chartConfig.stripe.color}
                  share={stripeShareArs}
                  amount={fmtUsd(stripeAllUsdCents)}
                />
                <ShareRow
                  label={gatewayMoneyLabel("mercadopago")}
                  color={chartConfig.mercadopago.color}
                  share={mpShareArs}
                  amount={fmtArs(mpAllArsCents)}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>

        <CurrencyBreakdownCard
          title={`${gatewayMoneyLabel("stripe")} · monedas nativas`}
          accent={chartConfig.stripe.color}
          items={mainUsd?.stripe.by_currency ?? []}
          isLoading={isLoading}
        />
        <CurrencyBreakdownCard
          title={`${gatewayMoneyLabel("mercadopago")} · monedas nativas`}
          accent={chartConfig.mercadopago.color}
          items={mainArs?.mercadopago.by_currency ?? []}
          isLoading={isLoading}
        />
      </div>
    </AdminPageShell>
  );
}

interface SeriesPoint {
  bucket: string;
  label: string;
  stripe?: number;
  mercadopago?: number;
  stripe_tx?: number;
  mercadopago_tx?: number;
}

function SingleGatewayChart({
  gateway,
  currencyLabel,
  view,
  isLoading,
  series,
  color,
  fmtCompact,
  fmtFull,
  weekly,
}: {
  gateway: "stripe" | "mercadopago";
  currencyLabel: "USD" | "ARS";
  view: "revenue" | "tx";
  isLoading: boolean;
  series: SeriesPoint[];
  color: string;
  fmtCompact: (n: number) => string;
  fmtFull: (cents: number) => string;
  weekly?: boolean;
}) {
  const dataKey = view === "revenue"
    ? gateway === "stripe" ? "stripe" : "mercadopago"
    : gateway === "stripe" ? "stripe_tx" : "mercadopago_tx";

  const title = gatewayMoneyLabel(gateway);
  const subtitle = view === "revenue" ? `Ingresos · ${currencyLabel} nativo` : "Cantidad de pagos";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color }}>
            {title}
          </p>
          <p className="text-xs text-white/40">{subtitle}</p>
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="h-[280px] w-full bg-white/[0.07]" />
      ) : series.length === 0 ? (
        <EmptyChart label="Sin pagos en este período" height={280} />
      ) : (
        <ChartContainer
          config={{ [gateway]: { label: title, color } } as ChartConfig}
          className="h-[280px] w-full"
        >
          <BarChart data={series} margin={{ left: 0, right: 8, top: 12, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/40" />
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={16}
              angle={series.length > 14 ? -30 : 0}
              textAnchor={series.length > 14 ? "end" : "middle"}
              height={series.length > 14 ? 50 : 30}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              width={70}
              tickFormatter={(v: number) =>
                view === "revenue"
                  ? fmtCompact(v)
                  : new Intl.NumberFormat("es-AR").format(v)
              }
            />
            <ChartTooltip
              cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_v, payload) => {
                    const iso = payload?.[0]?.payload?.bucket as string | undefined;
                    if (!iso) return "";
                    if (weekly) {
                      const start = new Date(`${iso}T00:00:00Z`);
                      const end = new Date(start);
                      end.setUTCDate(end.getUTCDate() + 6);
                      return `Semana ${start.toLocaleDateString("es-AR", {
                        day: "2-digit",
                        month: "long",
                        timeZone: "UTC",
                      })} – ${end.toLocaleDateString("es-AR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        timeZone: "UTC",
                      })}`;
                    }
                    return fmtDayLong(iso);
                  }}
                  formatter={(value) => {
                    const num = Number(value);
                    if (view === "revenue") {
                      return [` ${fmtFull(Math.round(num * 100))}`, title];
                    }
                    return [` ${new Intl.NumberFormat("es-AR").format(num)} pagos`, title];
                  }}
                  indicator="dot"
                />
              }
            />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}

function ShareRow({
  label,
  color,
  share,
  amount,
}: {
  label: string;
  color: string;
  share: number;
  amount: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: color }}
          />
          <span className="font-medium text-white/80">{label}</span>
        </div>
        <span className="tabular-nums text-white/45">
          {share.toFixed(1)}% · {amount}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.08]">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.max(0, Math.min(100, share))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function CurrencyBreakdownCard({
  title,
  accent,
  items,
  isLoading,
}: {
  title: string;
  accent: string;
  items: Array<{ currency: string; tx_count: number; revenue_cents: number }>;
  isLoading: boolean;
}) {
  const total = items.reduce((acc, x) => acc + (x.revenue_cents || 0), 0);
  return (
    <AdminGlassCard className="p-5">
      <div className="mb-4 flex items-center gap-2">
        <span
          aria-hidden
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: accent }}
        />
        <p className="text-sm font-semibold text-white/85">{title}</p>
      </div>
      <div className="space-y-3">
        {isLoading ? (
          <>
            <Skeleton className="h-6 w-full bg-white/[0.07]" />
            <Skeleton className="h-6 w-2/3 bg-white/[0.07]" />
          </>
        ) : items.length === 0 ? (
          <AdminEmptyState className="min-h-[64px] py-4">
            Sin transacciones todavía.
          </AdminEmptyState>
        ) : (
          items.map((c) => {
            const share = total > 0 ? (c.revenue_cents / total) * 100 : 0;
            return (
              <div key={c.currency} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium uppercase text-white/80">{c.currency}</span>
                  <span className="tabular-nums text-white/45">
                    {fmtCurrency(c.revenue_cents, c.currency)} · {fmtNumber(c.tx_count)} pagos
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.08]">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(0, Math.min(100, share))}%`,
                      backgroundColor: accent,
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </AdminGlassCard>
  );
}

function EmptyChart({ label, height }: { label: string; height: number }) {
  return (
    <div
      className="flex w-full items-center justify-center rounded-xl border border-dashed border-white/[0.1] bg-white/[0.02] text-sm text-white/40"
      style={{ height }}
      data-admin-surface="empty-chart"
    >
      {label}
    </div>
  );
}
